// Lister-Detail-Tab "Bilder" — Galerie der im Vario-DMS hinterlegten Bilder eines Artikels.
// Editierbar inline: Released-Toggle, Alt-Text, Reihenfolge. Speichern triggert denselben
// Backend-Pfad wie das Varianten-Bilder-Grid und der MCP (kaufland-release-images →
// vario.ensure_dms_share + vario.update_dms_resource_meta). Die frühere dritte Bedienstelle
// im Verkaufskanal-Slot ist seit 2026-08-16 entfernt (siehe docs/IMPLEMENTATION.md).

import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast } from '@vario-connect/frontend-design';
import { istFreigebbar, sperrHinweis, sperrgrund, zieheGesperrteZurueck } from '../../lib/bild-freigabe.js';

export interface ImagesTabCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
}

export interface ImagesTabListing {
  vario_article_id: string;
}

interface ImageRow {
  resourceId: string;
  alias: string;
  description: string | null;
  mimeType: string;
  fileSize: number | null;
  released: boolean;
  sortOrder: number | null;
  varioUiSort: number | null;
  publicUrl: string | null;
  /** Kauflands Bildvorgaben, bewertet von vario-article-images (fehlt bei aelteren Antworten). */
  releasable?: boolean;
  blockReason?: string | null;
  // Lokaler Edit-State:
  altText: string;
}

const COMPONENT_STYLES = `
  .kli-shell { padding: 1em; display: flex; flex-direction: column; gap: 0.8em; }
  .kli-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0.6em 0.8em;
    background: var(--v-color-surface, #fff);
    border: 1px solid var(--v-color-border, #e0e0e0);
    border-radius: var(--v-border-radius-card, 12px);
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  }
  .kli-list { display: flex; flex-direction: column; gap: 0.4em; }
  .kli-sperre-hinweis {
    padding: 0.5em 0.8em;
    border: 1px solid var(--v-color-warning, #e0a800);
    border-radius: var(--v-border-radius-card, 12px);
    background: var(--v-color-warning-container, #fff8e1);
    font-size: 0.85em;
  }
  .kli-toggle-row--gesperrt { opacity: 0.75; }
  .kli-sperre { color: var(--v-color-error, #b3261e); font-size: 0.8em; margin-top: 0.15em; }
  .kli-row {
    display: grid;
    grid-template-columns: 96px 1fr auto;
    gap: 1em;
    align-items: center;
    padding: 0.6em 0.8em;
    border: 1px solid var(--v-color-border, #e0e0e0);
    border-radius: 8px;
    background: var(--v-color-surface, #fff);
    border-left: 3px solid var(--v-positive, #84b933);
  }
  .kli-thumb {
    width: 96px; height: 96px; object-fit: cover;
    border-radius: 6px; border: 1px solid var(--v-color-border, #ddd);
    background: var(--v-color-surface-muted, #f7f7f7);
  }
  .kli-thumb.kli-thumb-loading {
    background: linear-gradient(110deg, #f0f0f0 8%, #e3e3e3 18%, #f0f0f0 33%);
    background-size: 200% 100%;
    animation: kli-shimmer 1.5s linear infinite;
  }
  @keyframes kli-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
  .kli-meta { display: flex; flex-direction: column; gap: 0.35em; min-width: 0; }
  .kli-alias { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .kli-slot {
    display: inline-block; padding: 0.1em 0.45em; margin-right: 0.3em;
    background: var(--v-positive-soft, #e8f3d6); color: var(--v-positive, #84b933);
    border-radius: 4px; font-size: 0.82em; font-weight: 600;
  }
  .kli-sub { font-size: 0.78em; color: var(--v-color-text-muted, #666); }
  .kli-alt-input {
    width: 100%; padding: 0.4em 0.6em;
    border: 1px solid var(--v-color-border, #ddd); border-radius: 4px;
    font-size: 0.92em;
  }
  .kli-actions { display: flex; flex-direction: column; gap: 0.3em; align-items: center; }
  .kli-empty { padding: 1.5em; text-align: center; color: var(--v-color-text-muted, #666); }
  .kli-tabs { display: flex; gap: 0.3em; border-bottom: 1px solid var(--v-color-border, #e0e0e0); margin-bottom: 0.4em; }
  .kli-tab {
    padding: 0.5em 1em; border: none; background: none; cursor: pointer;
    font-size: 0.95em; color: var(--v-color-text-muted, #666);
    border-bottom: 2px solid transparent;
  }
  .kli-tab.kli-tab--active { color: var(--v-color-text, #222); border-bottom-color: var(--v-positive, #84b933); font-weight: 600; }
  .kli-toggle-row {
    display: grid; grid-template-columns: 28px 64px 1fr; gap: 0.8em; align-items: center;
    padding: 0.6em 0.8em; border-bottom: 1px solid var(--v-color-border-soft, #eee);
  }
  .kli-toggle-thumb { width: 64px; height: 64px; object-fit: cover; border-radius: 6px; border: 1px solid var(--v-color-border, #ddd); background: var(--v-color-surface-muted, #f7f7f7); }
`;

async function callFn<T>(
  ctx: ImagesTabCtx,
  name: string,
  body: unknown,
): Promise<{ ok: true; value: T } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/${name}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let parsed: unknown;
    try { parsed = text ? JSON.parse(text) : {}; } catch { parsed = { raw: text }; }
    if (!res.ok) {
      const errBag = (parsed as { error?: { code?: string; message?: string } } | null)?.error;
      return { ok: false, error: errBag ? `${errBag.code ?? '?'}: ${errBag.message ?? '?'}` : `HTTP ${res.status}` };
    }
    return { ok: true, value: parsed as T };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

export async function renderImagesTab(
  host: HTMLElement,
  ctx: ImagesTabCtx,
  listing: ImagesTabListing,
): Promise<void> {
  host.innerHTML = `<style>${COMPONENT_STYLES}</style><div class="kli-shell"><div class="kli-empty">Lade Vario-DMS-Bilder …</div></div>`;

  type Loaded = Omit<ImageRow, 'altText'>;
  const res = await callFn<{ ok: boolean; images: Loaded[] }>(ctx, 'vario-article-images', {
    articleId: listing.vario_article_id,
  });
  if (!res.ok) {
    host.innerHTML = `<div class="kli-shell"><div class="v-text-error v-pa-md">Fehler: ${escapeHtml(res.error)}</div></div>`;
    return;
  }

  // rows hält ALLE Bilder (released + nicht released) als gemeinsamen State.
  // Der "Aktive Bilder"-Tab filtert beim Rendern auf released und sortiert nach
  // sortOrder; der "Freigeben"-Tab zeigt alle mit Toggle.
  const rows: ImageRow[] = (res.value.images ?? []).map((img) => ({
    ...img,
    altText: typeof img.description === 'string' ? img.description : '',
  }));
  // Kauflands Bildvorgaben (Format, Groesse; Bewertung aus vario-article-images): gesperrte
  // Bilder duerfen nicht freigegeben sein. Altfreigaben von vor der Regel werden hier
  // zurueckgenommen, geloescht werden sie erst mit dem naechsten Speichern -- deshalb gilt
  // der Freigeben-Reiter dann von Anfang an als geaendert.
  const zurueckgezogen = zieheGesperrteZurueck(rows);
  // Released-Bilder einmal initial nach vorhandenem sortOrder/varioUiSort ordnen
  // und auf 0..N normalisieren — danach ist sortOrder die alleinige Quelle der
  // Push-Reihenfolge.
  rows
    .filter((r) => r.released)
    .sort((a, b) => (a.sortOrder ?? a.varioUiSort ?? 0) - (b.sortOrder ?? b.varioUiSort ?? 0))
    .forEach((r, idx) => { r.sortOrder = idx; });
  // Wurden Altfreigaben zurueckgenommen, oeffnet der Reiter auf „Freigeben": Dort steht der
  // Hinweis, und dort ist der Speichern-Knopf aktiv. Auf „Aktive Bilder" waere das Bild nur
  // still verschwunden.
  let activeTab: 'active' | 'release' = zurueckgezogen.length > 0 ? 'release' : 'active';
  let dirtyActive = false;
  let dirtyRelease = zurueckgezogen.length > 0;

  // Cache der via Proxy geladenen Vorschau-Blob-URLs pro resourceId. Lebt fuer die
  // Dauer dieses Dialogs. Verhindert, dass jeder Tab-Wechsel / Checkbox-Toggle
  // dieselben Bilder erneut durch den DMS-Proxy laedt — sonst Vario-429 (Rate-Limit).
  const previewCache = new Map<string, string>();

  // Liefert das <img>-Markup fuer eine Vorschau: bevorzugt die public_url (freigegeben)
  // bzw. eine bereits gecachte Proxy-Blob-URL; sonst einen Lazy-Platzhalter, den
  // loadPreviewsViaProxy spaeter EINMAL befuellt und cacht.
  function previewImg(r: ImageRow, thumbClass: string): string {
    const ready = r.publicUrl ?? previewCache.get(r.resourceId) ?? null;
    if (ready) return `<img class="${thumbClass}" src="${escapeHtml(ready)}" alt="">`;
    return `<img class="${thumbClass} kli-thumb-loading img-proxy-lazy" data-rid="${escapeHtml(r.resourceId)}" alt="">`;
  }

  // Released-Bilder in Push-Reihenfolge (sortOrder aufsteigend, stabil).
  function releasedSorted(): ImageRow[] {
    return rows
      .filter((r) => r.released)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  // Vollständige releases-Payload aus dem gemeinsamen State — von BEIDEN
  // Speichern-Pfaden genutzt. Sendet alle released-Bilder mit fortlaufendem
  // sortOrder (0..N nach aktueller Push-Reihenfolge) + erhaltenem altText.
  // Was hier fehlt (= nicht released), löscht das Backend (Diff-Upsert).
  function buildReleases(): Array<{ varioResourceId: string; sortOrder: number; mimeType: string; alias: string; altText: string }> {
    return releasedSorted().map((r, idx) => ({
      varioResourceId: r.resourceId,
      sortOrder: idx,
      mimeType: r.mimeType,
      alias: r.alias,
      altText: r.altText.trim(),
    }));
  }

  function rerenderActive(): void {
    const shell = host.querySelector<HTMLElement>('.kli-body');
    if (!shell) return;
    const active = releasedSorted();
    if (active.length === 0) {
      shell.innerHTML = `
        <div class="kli-header">
          <strong>Bilder</strong>
          <span class="v-text-muted" style="font-size:0.85em;">Keine Bilder freigegeben.</span>
        </div>
        <div class="kli-empty">
          Wechsle in den Tab <em>Freigeben</em> und hake die Bilder an, die zu Kaufland sollen.
        </div>`;
      return;
    }
    shell.innerHTML = `
      <div class="kli-header">
        <div>
          <strong>Bilder</strong>
          <span class="v-text-muted" style="font-size:0.85em;margin-left:0.5em;">${active.length} ${active.length === 1 ? 'Bild' : 'Bilder'} freigegeben</span>
        </div>
        <div style="display:flex;gap:0.4em;">
          <button type="button" class="v-button" id="kli-reset" ${dirtyActive ? '' : 'disabled'}>
            <span class="v-icon fa-regular fa-rotate-left"></span> Zurücksetzen
          </button>
          <button type="button" class="v-button v-button--primary" id="kli-save" ${dirtyActive ? '' : 'disabled'}>
            <span class="v-icon fa-regular fa-floppy-disk"></span> Speichern
          </button>
        </div>
      </div>
      <div class="kli-list">
        ${active.map((r, idx) => renderRow(r, idx, active.length)).join('')}
      </div>
    `;
    wireRows(shell, active);
  }

  function renderRow(r: ImageRow, idx: number, total: number): string {
    const sizeKb = r.fileSize ? `${Math.round(r.fileSize / 1024)} KB` : '';
    // Freigegebene Bilder haben per Definition eine public_url (Worker hat das Share
    // angelegt). Direkter <img src=publicUrl>. Sehr seltener Edge-Case (Worker noch nicht
    // durch) → Proxy als Fallback mit Skeleton-Shimmer.
    const previewInner = previewImg(r, 'kli-thumb');
    // Slot-Bezeichnung: idx 0 = Hauptbild, idx 1 = Bild 1, idx 2 = Bild 2, ...
    // Diese Reihenfolge geht 1:1 ins picture-Array beim Kaufland-Push (sortiert nach sort_order).
    const slotLabel = idx === 0 ? 'Hauptbild' : `Bild ${idx}`;
    return `
      <div class="kli-row" data-rid="${escapeHtml(r.resourceId)}">
        ${previewInner}
        <div class="kli-meta">
          <div class="kli-alias" title="${escapeHtml(r.alias)}"><span class="kli-slot">${slotLabel}</span> · ${escapeHtml(r.alias)}</div>
          <div class="kli-sub">${escapeHtml(r.mimeType)}${sizeKb ? ` · ${sizeKb}` : ''}</div>
          <input type="text" class="kli-alt-input" data-rid="${escapeHtml(r.resourceId)}" value="${escapeHtml(r.altText)}" placeholder="Alt-Text fuer ${slotLabel} (Screenreader / SEO, geht ins Vario-DMS) …" maxlength="250">
        </div>
        <div class="kli-actions">
          <button type="button" class="v-button v-button--ghost v-icon-only kli-up" data-rid="${escapeHtml(r.resourceId)}" title="Nach oben" ${idx === 0 ? 'disabled' : ''}>
            <span class="v-icon fa-regular fa-arrow-up"></span>
          </button>
          <button type="button" class="v-button v-button--ghost v-icon-only kli-down" data-rid="${escapeHtml(r.resourceId)}" title="Nach unten" ${idx === total - 1 ? 'disabled' : ''}>
            <span class="v-icon fa-regular fa-arrow-down"></span>
          </button>
        </div>
      </div>`;
  }

  function wireRows(shell: HTMLElement, active: ImageRow[]): void {
    shell.querySelectorAll<HTMLInputElement>('input.kli-alt-input').forEach((inp) => {
      inp.addEventListener('input', () => {
        const r = rows.find((x) => x.resourceId === inp.dataset['rid']);
        if (r) { r.altText = inp.value; dirtyActive = true; updateSaveBtn(shell); }
      });
    });
    shell.querySelectorAll<HTMLButtonElement>('button.kli-up').forEach((b) => {
      b.addEventListener('click', () => move(b.dataset['rid'] ?? '', -1));
    });
    shell.querySelectorAll<HTMLButtonElement>('button.kli-down').forEach((b) => {
      b.addEventListener('click', () => move(b.dataset['rid'] ?? '', +1));
    });
    shell.querySelector<HTMLButtonElement>('#kli-save')?.addEventListener('click', () => void doSave());
    shell.querySelector<HTMLButtonElement>('#kli-reset')?.addEventListener('click', () => void renderImagesTab(host, ctx, listing));
    void loadPreviewsViaProxy(shell);
    // active-Param nur fuer Symmetrie/Lesbarkeit; Render nutzt bereits releasedSorted().
    void active;
  }

  function updateSaveBtn(shell: HTMLElement): void {
    const save = shell.querySelector<HTMLButtonElement>('#kli-save');
    const reset = shell.querySelector<HTMLButtonElement>('#kli-reset');
    if (save) save.disabled = !dirtyActive;
    if (reset) reset.disabled = !dirtyActive;
  }

  function move(rid: string, delta: number): void {
    const active = releasedSorted();
    const pos = active.findIndex((r) => r.resourceId === rid);
    const newPos = pos + delta;
    if (pos < 0 || newPos < 0 || newPos >= active.length) return;
    // sortOrder der beiden Nachbarn tauschen — danach normalisieren wir auf 0..N,
    // damit die Werte kompakt bleiben.
    const a = active[pos]!;
    const b = active[newPos]!;
    const tmp = a.sortOrder ?? pos;
    a.sortOrder = b.sortOrder ?? newPos;
    b.sortOrder = tmp;
    releasedSorted().forEach((r, idx) => { r.sortOrder = idx; });
    dirtyActive = true;
    rerenderActive();
  }

  async function loadPreviewsViaProxy(shell: HTMLElement): Promise<void> {
    const imgs = Array.from(shell.querySelectorAll<HTMLImageElement>('img.img-proxy-lazy'));
    // Sequenziell (Concurrency 1) statt parallel — der DMS-Proxy macht pro Bild einen
    // Vario-DMS-Call + OAuth-Refresh; ein Burst ueberschreitet das Vario-Rate-Limit (429).
    // Jede geladene Blob-URL wird in previewCache abgelegt, sodass Tab-Wechsel / Toggles
    // dasselbe Bild NICHT erneut laden (previewImg() rendert dann direkt aus dem Cache).
    for (const imgEl of imgs) {
      const rid = imgEl.dataset['rid'];
      if (!rid) continue;
      if (!imgEl.isConnected) continue;
      // Schon im Cache (z.B. Race mit einem frueheren Render): direkt setzen, kein Fetch.
      const cached = previewCache.get(rid);
      if (cached) {
        imgEl.src = cached;
        imgEl.classList.remove('kli-thumb-loading');
        continue;
      }
      try {
        const r = await fetch(`${ctx.supabaseUrl}/functions/v1/vario-dms-image-proxy`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
          body: JSON.stringify({ articleId: listing.vario_article_id, resourceId: rid }),
        });
        if (!r.ok) continue;
        const url = URL.createObjectURL(await r.blob());
        previewCache.set(rid, url);
        if (!imgEl.isConnected) continue;
        imgEl.src = url;
        imgEl.classList.remove('kli-thumb-loading');
      } catch (e) {
        console.warn('[kaufland-list-images] preview proxy failed for', rid, (e as Error).message);
      }
    }
  }

  async function doSave(): Promise<void> {
    const saveBtn = host.querySelector<HTMLButtonElement>('#kli-save');
    if (saveBtn) saveBtn.disabled = true;
    // Vollständige released-Liste aus dem gemeinsamen State (Datenverlust-Schutz):
    // erhält Alt-Texte + Reihenfolge, löscht nur wirklich abgewählte Bilder.
    const releases = buildReleases();
    const res = await callFn<{ ok: boolean; inserted: number; updated: number; deleted: number; enqueuedShareJobs?: number; enqueuedMetaJobs?: number }>(
      ctx,
      'kaufland-release-images',
      { articleId: listing.vario_article_id, releases },
    );
    if (!res.ok) {
      showToast(`Speichern fehlgeschlagen: ${res.error}`, 'error');
      if (saveBtn) saveBtn.disabled = false;
      return;
    }
    const v = res.value;
    showToast(
      `Bilder gespeichert (${v.inserted} neu, ${v.updated} sortiert, ${v.deleted} entfernt${v.enqueuedShareJobs ? `, ${v.enqueuedShareJobs} neue Shares` : ''}${v.enqueuedMetaJobs ? `, ${v.enqueuedMetaJobs} DMS-Updates` : ''}).`,
      'success',
    );
    dirtyActive = false;
    void renderImagesTab(host, ctx, listing);
  }

  function rerenderRelease(): void {
    const body = host.querySelector<HTMLElement>('.kli-body');
    if (!body) return;
    if (rows.length === 0) {
      body.innerHTML = `
        <div class="kli-header"><strong>Freigeben</strong></div>
        <div class="kli-empty">
          Keine Bilder am Artikel. Bitte erst in Vario unter
          <em>Artikel-Detail → Anlagen</em> Bilder hochladen (JPEG, PNG, GIF, WebP oder TIFF, höchstens 10 MB).
        </div>`;
      return;
    }
    const releasedCount = rows.filter((r) => r.released).length;
    const gesperrt = rows.filter((r) => !istFreigebbar(r)).length;
    const hinweis = sperrHinweis(gesperrt, zurueckgezogen.length);
    body.innerHTML = `
      <div class="kli-header">
        <div>
          <strong>Freigeben</strong>
          <span class="v-text-muted" style="font-size:0.85em;margin-left:0.5em;">${releasedCount} von ${rows.length} freigegeben</span>
        </div>
        <button type="button" class="v-button v-button--primary" id="kli-rel-save" ${dirtyRelease ? '' : 'disabled'}>
          <span class="v-icon fa-regular fa-floppy-disk"></span> Auswahl speichern
        </button>
      </div>
      ${hinweis ? `<div class="kli-sperre-hinweis">${escapeHtml(hinweis)}</div>` : ''}
      <div class="kli-list">
        ${rows.map((r) => renderToggleRow(r)).join('')}
      </div>`;
    wireReleaseRows(body);
  }

  function renderToggleRow(r: ImageRow): string {
    const sizeKb = r.fileSize ? `${Math.round(r.fileSize / 1024)} KB` : '';
    const previewInner = previewImg(r, 'kli-toggle-thumb');
    // Gesperrt (Format, Groesse): Schalter aus und leer, der Grund steht unter dem Dateityp.
    const frei = istFreigebbar(r);
    const schalter = frei
      ? `<input type="checkbox" class="kli-rel-toggle" data-rid="${escapeHtml(r.resourceId)}" ${r.released ? 'checked' : ''} style="cursor:pointer;width:18px;height:18px;">`
      : `<input type="checkbox" class="kli-rel-toggle" data-rid="${escapeHtml(r.resourceId)}" disabled title="${escapeHtml(sperrgrund(r))}" style="width:18px;height:18px;">`;
    return `
      <div class="kli-toggle-row${frei ? '' : ' kli-toggle-row--gesperrt'}" data-rid="${escapeHtml(r.resourceId)}">
        ${schalter}
        ${previewInner}
        <div class="kli-meta">
          <div class="kli-alias" title="${escapeHtml(r.alias)}">${escapeHtml(r.alias)}</div>
          <div class="kli-sub">${escapeHtml(r.mimeType)}${sizeKb ? ` · ${sizeKb}` : ''}</div>
          ${frei ? '' : `<div class="kli-sperre">${escapeHtml(sperrgrund(r))}</div>`}
        </div>
      </div>`;
  }

  function wireReleaseRows(body: HTMLElement): void {
    body.querySelectorAll<HTMLInputElement>('input.kli-rel-toggle').forEach((cb) => {
      cb.addEventListener('change', () => {
        const r = rows.find((x) => x.resourceId === cb.dataset['rid']);
        if (!r) return;
        if (!istFreigebbar(r)) { cb.checked = false; return; }
        r.released = cb.checked;
        if (cb.checked) {
          // Neu freigegeben → ans Ende der Push-Reihenfolge.
          const maxSort = rows.filter((x) => x.released && x !== r)
            .reduce((m, x) => Math.max(m, x.sortOrder ?? 0), -1);
          r.sortOrder = maxSort + 1;
        }
        dirtyRelease = true;
        rerenderRelease();
      });
    });
    body.querySelector<HTMLButtonElement>('#kli-rel-save')?.addEventListener('click', () => void doSaveRelease());
    void loadPreviewsViaProxy(body);
  }

  async function doSaveRelease(): Promise<void> {
    const saveBtn = host.querySelector<HTMLButtonElement>('#kli-rel-save');
    if (saveBtn) saveBtn.disabled = true;
    // Vollständige released-Liste — erhält Reihenfolge + Alt-Texte der bestehenden,
    // fügt neu angehakte hinzu, lässt abgewählte weg (Backend löscht sie).
    const releases = buildReleases();
    const res = await callFn<{ ok: boolean; inserted: number; updated: number; deleted: number; enqueuedShareJobs?: number; enqueuedMetaJobs?: number }>(
      ctx,
      'kaufland-release-images',
      { articleId: listing.vario_article_id, releases },
    );
    if (!res.ok) {
      showToast(`Speichern fehlgeschlagen: ${res.error}`, 'error');
      if (saveBtn) saveBtn.disabled = false;
      return;
    }
    const v = res.value;
    showToast(
      `Auswahl gespeichert (${v.inserted} freigegeben, ${v.deleted} entfernt).`,
      'success',
    );
    dirtyRelease = false;
    void renderImagesTab(host, ctx, listing);
  }

  function renderShell(): void {
    host.innerHTML = `
      <style>${COMPONENT_STYLES}</style>
      <div class="kli-shell">
        <div class="kli-tabs">
          <button type="button" class="kli-tab ${activeTab === 'active' ? 'kli-tab--active' : ''}" data-tab="active">Aktive Bilder</button>
          <button type="button" class="kli-tab ${activeTab === 'release' ? 'kli-tab--active' : ''}" data-tab="release">Freigeben</button>
        </div>
        <div class="kli-body"></div>
      </div>`;
    const tabs = Array.from(host.querySelectorAll<HTMLButtonElement>('.kli-tab'));
    tabs.forEach((btn) => {
      btn.addEventListener('click', () => {
        const t = btn.dataset['tab'];
        if (t !== 'active' && t !== 'release') return;
        if (t === activeTab) return;
        activeTab = t;
        // Nur die aktive-Klasse umschalten + Body neu rendern — die Shell (inkl.
        // <style>) bleibt stehen, damit laufende Proxy-Fetches nicht verwaisen.
        tabs.forEach((b) => b.classList.toggle('kli-tab--active', b.dataset['tab'] === activeTab));
        renderBody();
      });
    });
    renderBody();
  }

  function renderBody(): void {
    if (activeTab === 'active') rerenderActive();
    else rerenderRelease();
  }

  renderShell();
}

function escapeHtml(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
